import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { SHARED_CALCULATOR_QUERY } from '../../graphql/queries';
import AdminCostCalculator from '../admin/AdminCostCalculator';
import BrandMark from '../../components/common/BrandMark';
import styles from './SharedCostCalculator.module.css';
import Icon from '../../components/common/Icon';

const DEAD_LINK: Record<string, string> = {
  expired: 'This link has expired.',
  revoked: 'This link has been revoked.',
};

// expiresAt is the start of the day after the last valid one.
const lastDay = (iso: string) => new Date(new Date(iso).getTime() - 1).toLocaleDateString('en-GB', {
  day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
});

// Public page: the token is the authorization, and the calculator itself asks
// the server with it, so nothing here reaches admin data.
const SharedCostCalculator = () => {
  const { token } = useParams<{ token: string }>();
  const { data, loading, error } = useQuery(SHARED_CALCULATOR_QUERY, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });
  const link = data?.sharedCalculator as { valid: boolean; status: string; label: string | null; expiresAt: string | null } | undefined;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <BrandMark size={28} decorative />
        <span className={styles.brand}>Infrio</span>
        <span className={styles.headerLabel}>Cost calculator</span>
        {link?.valid && link.label && <span className={styles.forLabel}>for {link.label}</span>}
      </header>

      <main className={styles.body}>
        {loading && !link ? (
          <div className={styles.centerState}>
            <Icon name="refresh" className="animate-spin mr-2" />
            Loading...
          </div>
        ) : error && !link ? (
          <div className={styles.centerState} role="alert">
            <Icon name="error" style={{ fontSize: 40 }} />
            <p>The calculator could not be opened: {error.message}</p>
          </div>
        ) : !link?.valid ? (
          <div className={styles.centerState}>
            <Icon name="link_off" style={{ fontSize: 40 }} />
            <p>{DEAD_LINK[link?.status ?? ''] ?? 'This link is invalid.'}</p>
          </div>
        ) : (
          <>
            {link.expiresAt && <p className={styles.validity}>This link works until {lastDay(link.expiresAt)}.</p>}
            <AdminCostCalculator shareToken={token} />
          </>
        )}
      </main>
    </div>
  );
};

export default SharedCostCalculator;
